import { useEffect } from 'react';

import { useSystemConfig } from '@/lib/queries';
import { fontFor, googleFontsHref } from '@/lib/fonts';

const LINK_ID = 'chicare-brand-fonts';

/**
 * Applies the typeface an admin chose, across the whole app.
 *
 * ── HOW IT WORKS, AND WHY IT IS NOT MORE CLEVER ─────────────────────────────
 *
 * The design reads `--font-display` and `--font-body` for every piece of text
 * on every screen (tokens.css). This sets those two variables on `:root` from
 * the public settings payload, and adds one stylesheet link for the families it
 * needs. That is the whole mechanism: nothing else in the app has to know a
 * font can change.
 *
 * ── AND WHY THE DEFAULT IS BAKED INTO THE PAGE, NOT FETCHED ─────────────────
 *
 * index.html ships with the default pairing already linked, so the first paint
 * is correct without waiting for /system/config. This runs afterwards and does
 * nothing at all unless an admin has chosen something different — which is the
 * common case, and the one that should cost nothing.
 *
 * On a slow connection an admin's choice therefore arrives a moment after the
 * page does. That is the right trade: text that is readable immediately in the
 * shipped face beats a blank screen waiting to find out which face to use.
 */
export function BrandFonts() {
  const { data: config } = useSystemConfig();

  const displayKey = config?.brand?.font_display;
  const bodyKey = config?.brand?.font_body;

  useEffect(() => {
    if (!displayKey && !bodyKey) return;

    const display = fontFor(displayKey, 'outfit');
    const body = fontFor(bodyKey, 'plus-jakarta');

    const href = googleFontsHref([display, body]);

    if (href) {
      // Reused rather than appended, so changing the setting twice in a session
      // leaves one link behind and not three.
      let link = document.getElementById(LINK_ID) as HTMLLinkElement | null;

      if (!link) {
        link = document.createElement('link');
        link.id = LINK_ID;
        link.rel = 'stylesheet';
        document.head.append(link);
      }

      if (link.href !== href) link.href = href;
    }

    const root = document.documentElement;

    root.style.setProperty('--font-display', display.stack);
    root.style.setProperty('--font-body', body.stack);

    // Deliberately no cleanup: unsetting these on unmount would flash the page
    // back to the shipped face on every route change. The component lives as
    // long as the app does, and a stylesheet link is cheap to leave in place.
  }, [displayKey, bodyKey]);

  return null;
}
